import React, { useEffect, useState } from 'react';

const API_ORIGIN = (process.env.REACT_APP_API_ENDPOINT || 'http://127.0.0.1:8000').replace(/\/api\/?$/, '');

export function resolveMediaSrc(src) {
  if (!src || typeof src !== 'string') return '';
  const trimmed = src.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('blob:') || trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  if (trimmed.startsWith('/media/')) return `${API_ORIGIN}${trimmed}`;
  const base = process.env.PUBLIC_URL || '';
  if (trimmed.startsWith('/')) return `${base}${trimmed}`;
  return `${base}/${trimmed.replace(/^\//, '')}`;
}

const DOCUMENT_EXTENSIONS = ['.pdf', '.doc', '.docx'];
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];

export function getMediaKind(src) {
  if (!src) return 'unknown';
  const path = src.split('?')[0].split('#')[0].toLowerCase();
  if (DOCUMENT_EXTENSIONS.some((ext) => path.endsWith(ext))) return 'document';
  if (IMAGE_EXTENSIONS.some((ext) => path.endsWith(ext))) return 'image';
  return 'unknown';
}

export function openMediaInNewTab(src) {
  const resolved = resolveMediaSrc(src);
  if (!resolved) return false;
  window.open(resolved, '_blank', 'noopener,noreferrer');
  return true;
}

/** Images open in the preview modal; PDFs and Word docs open in a new browser tab. */
export function handleMediaView(src, { onPreview, title, subtitle }) {
  const resolved = resolveMediaSrc(src);
  if (!resolved) return { ok: false, reason: 'missing' };

  const kind = getMediaKind(resolved);
  if (kind === 'document') {
    openMediaInNewTab(resolved);
    return { ok: true, kind: 'document' };
  }

  onPreview({ src: resolved, title: title || 'Preview', subtitle: subtitle || '' });
  return { ok: true, kind: kind === 'image' ? 'image' : 'unknown' };
}

function MediaPreviewModal({ open, src, title, subtitle, onClose }) {
  const [loadError, setLoadError] = useState(false);
  const resolved = resolveMediaSrc(src);

  useEffect(() => {
    if (open) setLoadError(false);
  }, [open, src]);

  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6">
      <button
        type="button"
        aria-label="Close preview"
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title || 'Media preview'}
        className="relative z-10 flex w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl max-h-[92vh]"
      >
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 bg-white px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-base font-black tracking-tight text-slate-900">{title || 'Preview'}</h2>
            {subtitle ? <p className="mt-0.5 truncate text-xs text-slate-500">{subtitle}</p> : null}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {resolved ? (
              <a
                href={resolved}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:border-violet-200 hover:bg-violet-50 hover:text-violet-700"
              >
                Open in tab
              </a>
            ) : null}
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-xl text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
            >
              ×
            </button>
          </div>
        </div>

        <div className="flex min-h-[220px] flex-1 items-center justify-center overflow-auto bg-slate-100 p-4 sm:p-6">
          {!resolved || loadError ? (
            <div className="px-6 py-10 text-center">
              <p className="text-3xl opacity-40" aria-hidden>🖼️</p>
              <p className="mt-3 text-sm font-semibold text-slate-600">Preview unavailable</p>
              <p className="mt-1 text-xs text-slate-400">The file could not be loaded or does not exist.</p>
            </div>
          ) : (
            <img
              src={resolved}
              alt={subtitle ? `Preview for ${subtitle}` : 'Media preview'}
              className="max-h-[72vh] w-auto max-w-full rounded-lg object-contain shadow-md"
              onError={() => setLoadError(true)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default MediaPreviewModal;
